import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Chart grid layout configuration
export const CHART_MIN_WIDTH = 200; // Minimum width of a single chart in pixels (wraps below this)
export const CHART_MAX_WIDTH = 500; // Maximum width of a single chart in pixels
export const CHARTS_PER_ROW = 4; // Maximum number of charts per row
export const CHART_HEIGHT = 200; // Height of the chart area in pixels

export interface SeriesData {
  metric: Record<string, string>;
  values: [number, string][];
}

export interface QueryRangeResponse {
  status: string;
  data: {
    resultType: string;
    result: SeriesData[];
  };
}

export interface ChartDataPoint {
  timestamp: number;
  formattedTime: string;
  [key: string]: number | string;
}

export const CHART_COLORS = [
  "#8884d8", // purple
  "#82ca9d", // green
  "#ff7300", // orange
  "#0088FE", // blue
  "#00C49F", // teal
  "#FF8042", // coral
  "#a855f7", // violet
  "#ec4899", // pink
  "#14b8a6", // cyan
  "#f97316", // amber
];

export const ERROR_COLORS = [
  "#ff4d4f", // red
  "#ff7a45", // orange-red
  "#fa8c16", // orange
  "#d946ef", // fuchsia
  "#f43f5e", // rose
  "#10b981", // emerald
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#6366f1", // indigo
];

export const toRegex = (values: string[]): string => {
  if (values.length === 0) return ".*";
  return values.map((v) => `^${v}$`).join("|");
};

/**
 * Translate the generic "item" grouping to a chart-specific field name.
 * Each chart type has its own concept of an "item" (e.g., workload, volume_set, gvc).
 * @param grouping - The grouping array from filters
 * @param itemField - The field name to use when "item" is selected (e.g., "workload", "volume_set")
 * @returns A new grouping array with "item" replaced by the specific field name
 */
export const translateGrouping = (grouping: string[], itemField: string): string[] => {
  return grouping.map((g) => (g === "item" ? itemField : g));
};

/**
 * Build a Prometheus 'by' clause from labels.
 * Returns empty string if no labels provided (omit the clause entirely).
 * Example: buildByClause(["gvc", "workload"]) => "by (gvc, workload)"
 * Example: buildByClause([]) => ""
 */
export const buildByClause = (labels: string[]): string => {
  const filtered = labels.filter(Boolean);
  if (filtered.length === 0) return "";
  return `by (${filtered.join(", ")})`;
};

/**
 * Build a comma-separated label list for 'by' clauses that have required labels.
 * Filters out empty strings from the input.
 * Example: buildByLabels(["gvc", "workload"], ["le"]) => "gvc, workload, le"
 * Example: buildByLabels([], ["le"]) => "le"
 */
export const buildByLabels = (grouping: string[], requiredLabels: string[] = []): string => {
  const filtered = [...grouping.filter(Boolean), ...requiredLabels];
  return filtered.join(", ");
};

export const formatBytes = (value: number): string => {
  if (value >= 1024 * 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024 * 1024)).toFixed(1)} TB`;
  }
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} kB`;
  }
  return `${value.toFixed(0)} B`;
};

export const formatBytesTooltip = (value: number): string => {
  if (value >= 1024 * 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`;
  }
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (value >= 1024) {
    return `${(value / 1024).toFixed(2)} kB`;
  }
  return `${value.toFixed(2)} B`;
};

export const formatBytesBinary = (value: number): string => {
  if (value >= 1024 * 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024 * 1024)).toFixed(1)} TiB`;
  }
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GiB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
  }
  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KiB`;
  }
  return `${value.toFixed(0)} B`;
};

export const formatBytesBinaryTooltip = (value: number): string => {
  if (value >= 1024 * 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TiB`;
  }
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MiB`;
  }
  if (value >= 1024) {
    return `${(value / 1024).toFixed(2)} KiB`;
  }
  return `${value.toFixed(2)} B`;
};

export const formatBytesPerSecond = (value: number): string => {
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB/s`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB/s`;
  }
  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} kB/s`;
  }
  return `${value.toFixed(0)} B/s`;
};

export const formatBytesPerSecondTooltip = (value: number): string => {
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB/s`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(2)} MB/s`;
  }
  if (value >= 1024) {
    return `${(value / 1024).toFixed(2)} kB/s`;
  }
  return `${value.toFixed(2)} B/s`;
};

export const formatMillicores = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} cores`;
  }
  return `${value.toFixed(0)} m`;
};

export const formatPercent = (value: number): string => {
  return `${value.toFixed(0)}%`;
};

export const formatCount = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return `${value.toFixed(0)}`;
};

export const formatRequestsPerSecond = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k req/s`;
  }
  return `${value.toFixed(0)} req/s`;
};

export const formatLatency = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} s`;
  }
  return `${value.toFixed(0)} ms`;
};

export interface ProcessSeriesOptions {
  grouping: string[];
  timezoneValue: string;
  extraLabels?: string[];
  labelFormatter?: (metric: Record<string, string>, groupingPart: string) => string;
  valueMultiplier?: number;
  defaultLabel?: string;
  hideFromLabel?: string[];
}

/**
 * Choose a time format string based on the time span of the data.
 * - Under 24h: "HH:mm:ss" (time only)
 * - 24h and above: "MM/DD HH:mm" (date + time)
 */
export const getAdaptiveTimeFormat = (data: { timestamp: number }[]): string => {
  if (data.length < 2) return "HH:mm:ss";
  const spanMs = data[data.length - 1].timestamp - data[0].timestamp;
  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  if (spanMs <= ONE_DAY_MS) return "HH:mm:ss";
  return "MM/DD HH:mm";
};

export const processSeriesData = (
  data: SeriesData[],
  options: ProcessSeriesOptions
): { data: ChartDataPoint[]; keys: string[] } => {
  const { grouping, timezoneValue, labelFormatter, valueMultiplier = 1, defaultLabel, hideFromLabel = [] } = options;
  const timestampMap = new Map<number, ChartDataPoint>();
  const keys: string[] = [];

  data.forEach((series) => {
    // Build label from grouping fields, excluding hidden ones
    const labelGrouping = grouping.filter((g) => !hideFromLabel.includes(g));
    const groupingPart = labelGrouping
      .map((g) => series.metric[g] || "unknown")
      .filter(Boolean)
      .join("/");

    let seriesKey: string;
    if (labelFormatter) {
      seriesKey = labelFormatter(series.metric, groupingPart);
    } else if (groupingPart) {
      seriesKey = groupingPart;
    } else {
      seriesKey = defaultLabel || "Value";
    }

    if (!keys.includes(seriesKey)) {
      keys.push(seriesKey);
    }

    series.values.forEach(([timestamp, value]) => {
      const ts = timestamp * 1000;
      if (!timestampMap.has(ts)) {
        timestampMap.set(ts, {
          timestamp: ts,
          formattedTime: "",
        });
      }
      const point = timestampMap.get(ts)!;
      point[seriesKey] = parseFloat(value) * valueMultiplier;
    });
  });

  const sortedData = Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp);

  // Apply adaptive time format based on the actual data span
  const timeFormat = getAdaptiveTimeFormat(sortedData);
  sortedData.forEach((point) => {
    point.formattedTime = dayjs(point.timestamp).tz(timezoneValue).format(timeFormat);
  });

  return { data: sortedData, keys };
};

export interface BaseChartProps {
  org: string;
  workload?: string[];
  gvc?: string[];
  location?: string[];
  container?: string[];
  grouping?: string[];
  fromIso?: string | null;
  toIso?: string | null;
  step?: string;
}

export const getTimeRange = (fromIso: string | null | undefined, toIso: string | null | undefined) => {
  const end = toIso ? new Date(toIso) : new Date();
  const start = fromIso ? new Date(fromIso) : new Date(end.getTime() - 60 * 60 * 1000);
  return { start, end };
};

// Clean Prometheus step durations in ascending order of seconds
const STEP_SNAP_VALUES: [number, string][] = [
  [30, "30s"],
  [60, "1m"],
  [120, "2m"],
  [300, "5m"],
  [600, "10m"],
  [900, "15m"],
  [1800, "30m"],
  [3600, "1h"],
  [7200, "2h"],
  [21600, "6h"],
  [43200, "12h"],
  [86400, "1d"],
];

/**
 * Calculate the optimal Prometheus query step based on the time range and chart pixel width,
 * replicating Grafana's intervalMs / maxDataPoints logic.
 *
 * @param rangeMs - The time range in milliseconds (end - start)
 * @param maxDataPoints - The chart width in pixels (each pixel = 1 data point max)
 * @returns A clean Prometheus duration string (e.g., "30s", "5m", "1h")
 */
export const calculateStep = (rangeMs: number, maxDataPoints: number): string => {
  const safeMaxDataPoints = Math.max(1, maxDataPoints);
  const rawStepSeconds = Math.ceil(rangeMs / 1000 / safeMaxDataPoints);
  const stepSeconds = Math.max(30, rawStepSeconds);

  // Snap up to the nearest clean interval
  for (const [seconds, label] of STEP_SNAP_VALUES) {
    if (seconds >= stepSeconds) {
      return label;
    }
  }

  // Beyond 1d: snap to whole days
  const days = Math.ceil(stepSeconds / 86400);
  return `${days}d`;
};

/**
 * Extract error message from metrics API response or axios error
 */
export const getMetricsErrorMessage = (err: any): string => {
  // Check for axios error with response data
  if (err?.response?.data?.error) {
    return err.response.data.error;
  }
  // Check for direct error property (from non-success response)
  if (err?.error) {
    return err.error;
  }
  // Fall back to message or default
  return err?.message || "Failed to fetch metrics";
};
